import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngxs/store';
import { DistrictDTO } from 'src/app/common/dtos/district.dto';
import { noWhitespaceValidator } from 'src/app/common/utilities/forms/validators.forms';
import { UserRole } from 'src/app/common/state/user/role/user-role';
import { User } from 'src/app/common/state/user/user.model';
import { UserState } from 'src/app/common/state/user/user.state';
import { AlertService } from '../../../services/alert/alert.service';
import { SessionTypeService } from '../../../services/session-type/session-type.service';
import {
  CoachingLogType,
  CoachingSessionTypeDTO,
} from '../../../dtos/coaching-session.dto';
import {
  SESSION_TYPE_LAYOUT_INFO,
  SESSION_TYPE_LAYOUT_OPTIONS,
  SESSION_TYPE_LAYOUT_THUMBNAILS,
  SessionTypeLayoutInfo,
} from '../../../helpers/session-type/session-type-layout.constants';
import { ModalComponent, ModalConfig } from '../modal/modal.component';

@Component({
  selector: 'app-create-edit-session-type-modal',
  templateUrl: './create-edit-session-type-modal.component.html',
  styleUrls: ['./create-edit-session-type-modal.component.scss'],
  standalone: false,
})
export class CreateEditSessionTypeModalComponent implements OnInit {
  @ViewChild('modal') modal: ModalComponent;

  // The district whose Session Types page this modal was opened from — not
  // necessarily the current user's own district (eSuite Admins and Operators
  // manage other districts via districtCode routing on the page).
  @Input() district: DistrictDTO | undefined;

  @Output() readonly sessionTypeSaved = new EventEmitter<void>();

  isEditMode = false;

  sessionTypeToEdit: CoachingSessionTypeDTO | undefined;

  form: FormGroup;

  submitLoading = false;

  layoutOptions = SESSION_TYPE_LAYOUT_OPTIONS;

  layoutInfo = SESSION_TYPE_LAYOUT_INFO;

  layoutThumbnails = SESSION_TYPE_LAYOUT_THUMBNAILS;

  readonly coachingLockedTooltip =
    'This setting cannot be changed as it affects reporting data';

  modalConfig: ModalConfig = {
    titleText: 'Create a Session Type',
    headerClass: ['modal-header', 'bg-primary', 'text-white'],
    customFooter: true,
    options: { size: 'lg' },
  };

  private user: User;

  constructor(
    private formBuilder: FormBuilder,
    private sessionTypeService: SessionTypeService,
    private alertService: AlertService,
    private store: Store
  ) {
    this.user = this.store.selectSnapshot(UserState.getUser) as User;
  }

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      title: [
        '',
        [Validators.required, noWhitespaceValidator, Validators.maxLength(255)],
      ],
      description: [''],
      layout: [this.layoutOptions[0].value, Validators.required],
      isCoaching: [false],
      isShared: [false],
    });

    this.form.get('layout')?.valueChanges.subscribe((layout) => {
      if (layout !== 'general-log') {
        this.form.get('isCoaching')?.setValue(false);
      }
    });
  }

  get canShowSharedToggle(): boolean {
    if (!this.user) {
      return false;
    }
    if (this.user.roles.includes(UserRole.E2L_EMPLOYEE)) {
      return true;
    }
    return (
      (this.user.roles.includes(UserRole.OPERATOR) ||
        this.user.roles.includes(UserRole.REMOTE_OPERATOR)) &&
      !!this.district?.isB2b
    );
  }

  get sharedToggleHelpText(): string {
    // Reach depends on the owning district's B2B status, not who created it
    // (see actionIndex's visibility rule in CoachlogTypeController) — a B2B
    // district's shared types only ever reach its own family, even when an
    // e2L employee is the one creating it on that district's behalf.
    if (this.district?.isB2b) {
      return 'Make this session type available to every district in your B2B family — not every district in GroweLab.';
    }
    return 'Make this session type available to every district.';
  }

  getLayoutThumbnail(layout: CoachingLogType): string | undefined {
    return this.layoutThumbnails[layout];
  }

  get showCoachingToggle(): boolean {
    return this.form?.get('layout')?.value === 'general-log';
  }

  get selectedLayoutInfo(): SessionTypeLayoutInfo | undefined {
    const layout = this.form?.get('layout')?.value;
    return layout ? this.layoutInfo[layout] : undefined;
  }

  openCreate(): void {
    this.isEditMode = false;
    this.sessionTypeToEdit = undefined;
    this.modalConfig.titleText = 'Create a Session Type';
    this.resetForm();
    this.modal.loadConfig();
    this.modal.open();
  }

  openEdit(sessionType: CoachingSessionTypeDTO): void {
    this.isEditMode = true;
    this.sessionTypeToEdit = sessionType;
    this.modalConfig.titleText = 'Edit Session Type';
    this.form.reset({
      title: sessionType.title,
      description: sessionType.description || '',
      layout: sessionType.view,
      isCoaching: sessionType.isCoaching,
      isShared: sessionType.isShared || false,
    });
    this.form.get('layout')?.disable();
    this.form.get('isCoaching')?.disable();
    this.modal.loadConfig();
    this.modal.open();
  }

  selectLayout(layout: CoachingLogType): void {
    if (this.isEditMode) {
      return;
    }
    this.form.get('layout')?.setValue(layout);
  }

  closeModal(): void {
    this.modal.close();
  }

  onSubmit(): void {
    if (this.form.invalid || this.submitLoading) {
      return;
    }

    this.submitLoading = true;
    const { title, description, layout, isCoaching, isShared } =
      this.form.getRawValue();

    if (this.isEditMode && this.sessionTypeToEdit) {
      this.sessionTypeService
        .updateSessionType(this.sessionTypeToEdit.id, {
          name: title,
          description,
          ...(this.canShowSharedToggle ? { is_shared: isShared } : {}),
        })
        .subscribe({
          next: () => {
            this.submitLoading = false;
            this.modal.close();
            this.sessionTypeSaved.emit();
            this.alertService.showAlert('Session Type Updated');
          },
          error: () => {
            this.submitLoading = false;
          },
        });
    } else {
      this.sessionTypeService
        .createSessionType({
          name: title,
          description,
          layout,
          is_coaching: layout === 'general-log' ? isCoaching : false,
          is_shared: this.canShowSharedToggle ? isShared : false,
          district_id: this.district?.id,
        })
        .subscribe({
          next: () => {
            this.submitLoading = false;
            this.modal.close();
            this.sessionTypeSaved.emit();
            this.alertService.showAlert('Session Type Created');
          },
          error: () => {
            this.submitLoading = false;
          },
        });
    }
  }

  resetForm(): void {
    this.form.reset({
      title: '',
      description: '',
      layout: this.layoutOptions[0].value,
      isCoaching: false,
      isShared: false,
    });
    this.form.get('layout')?.enable();
    this.form.get('isCoaching')?.enable();
    this.submitLoading = false;
  }
}
